import { useState } from 'react';
import { NOMS_DES_JOURS, SEMAINE_AFFICHEE } from '@colette/shared';
import { C, COULEUR_DU_JOUR, DEGRADE_ACTION } from '../../theme';
import { Squelette, Panne, Vide } from '../composants/base';
import { BoutonDeuxGestes } from '../composants/BoutonDeuxGestes';
import { FormulaireRoutine } from '../composants/FormulaireRoutine';
import { api } from '../../api/routes';
import { useDonnees, messageDErreur } from '../../api/useDonnees';
import type { Routine } from '../../api/types';

/**
 * « Ma semaine » — la semaine type, geree a la main (spec routines, jalon 2).
 *
 * Sept sections, lundi a dimanche. Une routine peut figurer sous PLUSIEURS jours : c'est
 * une seule serie, montree partout ou elle tombe. La modifier ou la retirer depuis
 * n'importe lequel de ces jours agit sur la serie entiere — l'ecran le dit.
 *
 * AUCUN appel d'intelligence artificielle sur cet ecran.
 */

/** « 19:00 » et « 20:00 » -> minutes occupees. Sans fin, une routine ne compte pas d'heures. */
function dureeEnMinutes(routine: Routine): number {
  if (routine.fin === null) return 0;
  const enMinutes = (heure: string) => {
    const [h, m] = heure.split(':').map(Number);
    return h! * 60 + m!;
  };
  return Math.max(enMinutes(routine.fin) - enMinutes(routine.debut), 0);
}

/** Le total d'une semaine, arrondi a la demi-heure la plus proche pour rester lisible. */
export function totalDeLaSemaine(routines: Routine[]): string | null {
  const minutes = routines.reduce(
    (somme, routine) => somme + dureeEnMinutes(routine) * routine.jours.length,
    0,
  );
  if (minutes === 0) return null;
  const heures = Math.floor(minutes / 60);
  const reste = minutes % 60;
  if (heures === 0) return `${reste} min occupées cette semaine`;
  return reste === 0
    ? `${heures} h occupées cette semaine`
    : `${heures} h ${String(reste).padStart(2, '0')} occupées cette semaine`;
}

export function MaSemaine({
  version,
  signaler,
  rafraichir,
}: {
  version: number;
  signaler: (message: string) => void;
  rafraichir: () => void;
}) {
  const etat = useDonnees((signal) => api.routines(signal), version);
  const [ajoutPour, setAjoutPour] = useState<number | null>(null);
  const [enEdition, setEnEdition] = useState<string | null>(null);

  if (etat.chargement && etat.donnees === null) {
    return <Squelette lignes={4} texte="Chargement de ta semaine…" />;
  }
  if (etat.erreur !== null && etat.donnees === null) {
    return <Panne message={etat.erreur} onReessayer={etat.recharger} />;
  }

  const routines = etat.donnees?.routines ?? [];
  const total = totalDeLaSemaine(routines);

  const apresEcriture = (message: string) => {
    setAjoutPour(null);
    setEnEdition(null);
    signaler(message);
    // Le planning et Aujourd'hui en dependent : on rafraichit tout.
    rafraichir();
  };

  const retirer = async (routine: Routine) => {
    try {
      await api.supprimerEvenement(routine.id);
      apresEcriture('Routine retirée');
    } catch (cause) {
      signaler(messageDErreur(cause));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 13, color: C.secondaire, lineHeight: 1.5 }}>
        Ce qui revient chaque semaine. Colette s’en sert pour savoir quand tu es prise —
        elle ne t’enverra aucun rappel là-dessus, et rien de tout ceci ne part dans Google
        Agenda.
      </div>

      {total !== null ? (
        <div
          style={{
            background: C.carteDouce,
            border: `1px solid ${C.bordure}`,
            borderRadius: 14,
            padding: '10px 14px',
            fontSize: 13,
            fontWeight: 800,
            color: C.encre,
          }}
        >
          {total}
        </div>
      ) : null}

      {routines.length === 0 && ajoutPour === null ? (
        <Vide
          titre="Ta semaine est libre"
          texte="Ajoute ce qui revient chaque semaine : les repas, les trajets, les temps avec les enfants."
        />
      ) : null}

      {SEMAINE_AFFICHEE.map((jour) => {
        const duJour = routines
          .filter((routine) => routine.jours.includes(jour))
          .sort((a, b) => a.debut.localeCompare(b.debut));

        return (
          <div key={jour} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: COULEUR_DU_JOUR[jour],
                  flexShrink: 0,
                }}
              />
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  color: C.tertiaire,
                }}
              >
                {NOMS_DES_JOURS[jour]}
              </div>
            </div>

            {duJour.map((routine) =>
              enEdition === routine.id ? (
                <FormulaireRoutine
                  key={routine.id}
                  routine={routine}
                  onFait={apresEcriture}
                  onAnnuler={() => setEnEdition(null)}
                  signaler={signaler}
                />
              ) : (
                <LigneRoutine
                  key={routine.id}
                  routine={routine}
                  onModifier={() => setEnEdition(routine.id)}
                  onRetirer={() => void retirer(routine)}
                />
              ),
            )}

            {ajoutPour === jour ? (
              <FormulaireRoutine
                jourInitial={jour}
                onFait={apresEcriture}
                onAnnuler={() => setAjoutPour(null)}
                signaler={signaler}
              />
            ) : (
              <button
                type="button"
                className="pressable"
                onClick={() => setAjoutPour(jour)}
                style={{
                  alignSelf: 'flex-start',
                  border: `1px dashed ${C.bordureForte}`,
                  background: 'none',
                  color: C.secondaire,
                  borderRadius: 999,
                  padding: '6px 14px',
                  fontSize: 12.5,
                  fontWeight: 800,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                }}
              >
                + Ajouter une routine
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function LigneRoutine({
  routine,
  onModifier,
  onRetirer,
}: {
  routine: Routine;
  onModifier: () => void;
  onRetirer: () => void;
}) {
  // Une routine posée sur plusieurs jours est UNE serie : le dire evite de croire qu'on
  // ne touche qu'au jour affiche.
  const surPlusieursJours = routine.jours.length > 1;

  return (
    <div
      style={{
        background: C.carte,
        border: `1px solid ${C.bordure}`,
        borderRadius: 18,
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div
        style={{
          width: 46,
          fontSize: 13,
          fontWeight: 800,
          color: C.accent,
          flexShrink: 0,
        }}
      >
        {routine.debut}
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.encre }}>{routine.titre}</div>
        <div style={{ fontSize: 12, color: C.tertiaire }}>
          {routine.fin === null ? 'Sans heure de fin' : `Jusqu’à ${routine.fin}`}
          {surPlusieursJours ? ` · ${routine.jours.length} jours` : ''}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <button
          type="button"
          className="pressable"
          aria-label={`Modifier : ${routine.titre}`}
          onClick={onModifier}
          style={{
            border: 'none',
            background: 'none',
            padding: 0,
            fontSize: 11.5,
            fontWeight: 800,
            fontFamily: 'inherit',
            color: C.violetTexte,
            cursor: 'pointer',
          }}
        >
          Modifier
        </button>
        <BoutonDeuxGestes
          libelle="Retirer"
          libelleArme="Confirmer ?"
          aria={`Retirer : ${routine.titre}`}
          ariaArme={`Confirmer le retrait${surPlusieursJours ? ' de toute la routine' : ''} : ${routine.titre}`}
          onConfirmer={onRetirer}
          style={{
            border: 'none',
            background: 'none',
            padding: 0,
            fontSize: 11.5,
            fontWeight: 800,
            fontFamily: 'inherit',
            color: C.tertiaire,
            cursor: 'pointer',
          }}
          styleArme={{ color: C.corailTexte }}
        />
      </div>
    </div>
  );
}

export { DEGRADE_ACTION };
